import { RefundLeftPanelSide } from 'app/src/features/Shopping/Refund/components/RefundLeftPanelSide'
import { RefundRightPanelSide } from 'app/src/features/Shopping/Refund/components/RefundRightPanelSide'
import type { refundApi } from 'app/src/services/welfareportal'
import { euroFormatterIt, formatDate } from 'app/src/shared/helpers'
import { Checkbox, type CheckboxChangeEvent } from 'primereact/checkbox'
import { RadioButton, type RadioButtonChangeEvent } from 'primereact/radiobutton'
import { useTranslation } from 'react-i18next'

export function RefundConfirmation(props: {
  refund: refundApi.Refund
  additionalRadioKeys: string[] | undefined
  privacyStatementAccept: boolean
  setPrivacyStatementAccept: (flag: boolean) => void
  setAdditionalRadioKeys: (radioGroupIndex: number, additionalPropSelected: string) => void
  formData: {
    requestedRefund: number
    invoiceId: string
    documentDate: string
    vendorName: string
    vendorTaxId: string
    invoiceTotal: number
    customerTaxId?: string
  }
}) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Refund' })
  return (
    <div>
      <div className='card grid grid-cols-1 divide-x-0 divide-y-2 p-5 lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
        <RefundLeftPanelSide refund={props.refund} />
        <RefundRightPanelSide refund={props.refund} requestedRefund={props.formData.requestedRefund} funnelMode />
      </div>
      <div className='mt-4 grid rounded-md bg-[var(--card-background)] p-4'>
        <div>
          <h3 className='truncate'>{t('Funnel.Steps.ConfirmationStep.DetailBox.title')}</h3>
        </div>
        <div className='mt-3 grid grid-cols-1 md:grid-cols-2'>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.beneficiary')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>
                {props.refund.subject ? `${props.refund.subject.firstName} ${props.refund.subject.lastName}` : ''}
              </h3>
            </div>
          </div>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.invoiceId')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>{props.formData.invoiceId}</h3>
            </div>
          </div>
        </div>
        <div className='mt-3 grid grid-cols-1 md:grid-cols-2'>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.documentDate')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>{formatDate(props.formData.documentDate)}</h3>
            </div>
          </div>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.vendorName')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>{props.formData.vendorName}</h3>
            </div>
          </div>
        </div>
        <div className='mt-3 grid grid-cols-1 md:grid-cols-2'>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.vendorTaxId')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>{props.formData.vendorTaxId}</h3>
            </div>
          </div>
          <div className='grid-rows-2'>
            <div className='uppercase'>{t('Funnel.Steps.ConfirmationStep.DetailBox.invoiceTotal')}</div>
            <div className='mt-2'>
              <h3 className='truncate'>{euroFormatterIt.format(props.formData.invoiceTotal)}</h3>
            </div>
          </div>
        </div>
        {props.formData.customerTaxId && (
          <div className='mt-3 grid grid-cols-1 md:grid-cols-2'>
            <div className='grid-rows-2'>
              <div className='uppercase'>{props.refund.rule?.externalKeys?.find((v) => v.key === 'customerTaxId')?.label}</div>
              <div className='mt-2'>
                <h3 className='truncate'>{props.formData.customerTaxId}</h3>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* DYNAMIC CHECKBOX */}
      {props.refund?.rule?.additionalAttributes?.radio && (
        <div className='mt-6'>
          {props.refund.rule.additionalAttributes.radio.map((radio, radioIndex) => (
            <div key={radio.title ?? ''} className='mt-4'>
              {radio.title && <div dangerouslySetInnerHTML={{ __html: radio.title }} />}
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                {radio.attrs?.map((radioItem) =>
                  radioItem.name ? (
                    <div
                      key={radioItem.name}
                      className='justify-left mt-4 flex min-h-20 items-center rounded bg-[var(--card-background)] p-2'
                    >
                      <div className='flex items-center'>
                        <RadioButton
                          inputId={radioItem.name}
                          name={radioItem.name}
                          value={radioItem.name}
                          onChange={(e: RadioButtonChangeEvent) => {
                            if (typeof e.checked !== 'undefined' && e.checked) {
                              props.setAdditionalRadioKeys(radioIndex, radioItem.name!)
                            }
                          }}
                          checked={props.additionalRadioKeys?.[radioIndex] === radioItem.name}
                          required
                        />
                        <label htmlFor={radioItem.name} className='ml-2 cursor-pointer'>
                          {<div dangerouslySetInnerHTML={{ __html: radioItem.text ?? '' }} />}
                        </label>
                      </div>
                    </div>
                  ) : (
                    <></>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {props.refund?.rule?.termsAndConditions && (
        <>
          <div className='mt-4'>
            <div>
              <div dangerouslySetInnerHTML={{ __html: props.refund?.rule?.termsAndConditions }} />
            </div>
          </div>
          <div className='justify-content-center mt-[30px] flex flex-wrap gap-3'>
            <div className='align-items-center flex cursor-pointer items-center'>
              <Checkbox
                required
                inputId='privacyStatementAccept'
                checked={props.privacyStatementAccept}
                onChange={(event: CheckboxChangeEvent) => {
                  props.setPrivacyStatementAccept(typeof event.checked !== 'undefined' ? event.checked : false)
                }}
              />
              <label
                htmlFor='privacyStatementAccept'
                className='ml-2 cursor-pointer'
                dangerouslySetInnerHTML={{ __html: t('Funnel.Steps.ConfirmationStep.DetailBox.privacyStatement') }}
              ></label>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
